/**
 * Ledger rules for paid song requests & payout rails — keep in sync with functions/trustPayments.js
 * (isPaidSongRequestForLedger, artist rail filtering).
 */

import { getCurrencyForCountry } from '@/lib/currency';

export const PLATFORM_COMMISSION = 0.3;
export const ARTIST_SHARE_RATE = 0.7;

/** Paid customer requests that should count toward gross (any provider). */
export function isPaidSongRequestForLedger(req) {
  const totalPaid = Number(req.total_paid);
  const amountPaid = Number(req.amount_paid || 0);
  const amount = !Number.isNaN(totalPaid) && totalPaid > 0 ? totalPaid : amountPaid;
  if (Number.isNaN(amount) || amount <= 0) return false;
  return Boolean(
    req.payment_intent_id ||
      req.transaction_reference ||
      req.gateway_xref ||
      req.order_reference
  );
}

/**
 * Admin console analytics (Recent Activity, Top Artists): paid customer checkouts only.
 * Excludes admin walkthrough lines (`is_admin_demo`), trial sandbox (`is_trial`), and
 * artist "mark as played" / manual rows without a payment reference.
 */
export function isAdminAnalyticsPaidRequest(req) {
  if (!req || req.is_admin_demo === true) return false;
  if (req.is_trial === true) return false;
  return isPaidSongRequestForLedger(req);
}

/** Currency on a paid request. Missing currency + legacy paypal_us → USD. */
export function ledgerCurrencyForRequest(req) {
  const cur = String(req?.currency || '').toUpperCase();
  if (cur) return cur;
  if (String(req?.payment_provider || '').toLowerCase() === 'paypal_us') return 'USD';
  return 'GBP';
}

/** Paid requests that count on this artist's payout rail (matched by country currency). */
export function isPaidSongRequestForArtistLedger(req, profile) {
  if (!isPaidSongRequestForLedger(req)) return false;
  const expected = getCurrencyForCountry(profile?.country);
  return ledgerCurrencyForRequest(req) === expected;
}

/** When summing payout requests for an artist, only include docs in their rail. */
export function payoutDocMatchesArtistRail(payout, profile) {
  const expected = getCurrencyForCountry(profile?.country);
  const cur = String(payout?.currency || 'GBP').toUpperCase();
  return cur === expected;
}

export const MIN_MANUAL_PAYOUT_GBP = 10;
export const MIN_MANUAL_PAYOUT_USD = 20;

export const ADMIN_LEDGER_FIELDS = {
  gross: { adjKey: 'admin_ledger_gross_adjustment', editedKey: 'gross' },
  artistShare: { adjKey: 'admin_ledger_share_adjustment', editedKey: 'artistShare' },
  paidOut: { adjKey: 'admin_ledger_paid_out_adjustment', editedKey: 'paidOut' },
  reserved: { adjKey: 'admin_ledger_reserved_adjustment', editedKey: 'reserved' },
  currentBalance: { adjKey: 'admin_ledger_adjustment', editedKey: 'currentBalance' },
};

function moneyNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function hasAdminLedgerEdit(profile, field) {
  if (profile?.admin_ledger_edited?.[ADMIN_LEDGER_FIELDS[field].editedKey] === true) return true;
  // Early current-balance edits stored the offset without the edited map.
  if (field === 'currentBalance' && Number(profile?.admin_ledger_adjustment)) return true;
  return false;
}

/**
 * Applies any admin baselines on top of live ledger totals. Unedited fields stay
 * live. Edited fields keep a stored offset so new requests/payouts still move
 * the number from the amount the admin saved.
 */
export function applyAdminLedgerDisplay(live, profile) {
  const round = (n) => Number(moneyNumber(n).toFixed(2));
  const adj = (field) => moneyNumber(profile?.[ADMIN_LEDGER_FIELDS[field].adjKey]);
  const liveGross = round(live.gross);
  const liveShare = round(live.artistShare);
  const livePaidOut = round(live.paidOut);
  const liveReserved = round(live.reserved);

  const gross = hasAdminLedgerEdit(profile, 'gross')
    ? Math.max(0, round(liveGross + adj('gross')))
    : liveGross;
  const artistShare = hasAdminLedgerEdit(profile, 'artistShare')
    ? Math.max(0, round(liveShare + adj('artistShare')))
    : liveShare;
  const paidOut = hasAdminLedgerEdit(profile, 'paidOut')
    ? Math.max(0, round(livePaidOut + adj('paidOut')))
    : livePaidOut;
  const reserved = hasAdminLedgerEdit(profile, 'reserved')
    ? Math.max(0, round(liveReserved + adj('reserved')))
    : liveReserved;
  const currentBalance = hasAdminLedgerEdit(profile, 'currentBalance')
    ? Math.max(0, round(liveShare - livePaidOut - liveReserved + adj('currentBalance')))
    : Math.max(0, round(liveShare - livePaidOut - liveReserved));

  return {
    gross,
    artistShare,
    paidOut,
    reserved,
    currentBalance,
    hasAnyEdit: ['gross', 'artistShare', 'paidOut', 'reserved', 'currentBalance']
      .some((field) => hasAdminLedgerEdit(profile, field)),
  };
}

/** @deprecated use applyAdminLedgerDisplay — kept for existing Current Balance edits */
export function ledgerAdjustmentOf(profile) {
  return moneyNumber(profile?.admin_ledger_adjustment);
}

/** `artist_payout_requests` — default missing field = artist mode. */
export function payoutSourceOf(payout) {
  return String(payout?.payout_source || 'artist').toLowerCase() === 'karaoke' ? 'karaoke' : 'artist';
}
